import { Fragment, useEffect, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { fetchExecutionLog, type ExecutionLogEntry } from "../lib/executionLog.js";
import { STATUS_LABELS, STATUS_PILL_CLASSES, type ConnectorStatus } from "../lib/connectorVocab.js";
import { ErrorState, LoadingState } from "./States.js";

export type ConnectorEventTypeFilter = "all" | "connectivity" | "execution" | "command" | "state";

export type ConnectorEventRow = {
  id: string;
  execution_log_id?: string | null;
  ts: string;
  type: "connectivity" | "execution" | "command" | "state";
  level: "debug" | "info" | "warn" | "error";
  message: string;
  details?: Record<string, unknown> | null;
};

function fmtTimestamp(ts: string) {
  const when = new Date(ts).getTime();
  if (Number.isNaN(when)) return ts;
  return new Date(when).toLocaleString();
}

function labelCase(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function ConnectorEventLog(props: {
  connectorId: string;
  connectorName: string;
  connectorStatus: ConnectorStatus;
  headers?: Record<string, string>;
  apiBasePath?: string;
}) {
  const [filter, setFilter] = useState<ConnectorEventTypeFilter>("all");
  const [rows, setRows] = useState<ConnectorEventRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [executionByEventId, setExecutionByEventId] = useState<Record<string, ExecutionLogEntry | null>>({});
  const [executionLoadingByEventId, setExecutionLoadingByEventId] = useState<Record<string, boolean>>({});

  async function load(opts?: { silent?: boolean }) {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams();
      query.set("limit", "200");
      if (filter !== "all") query.set("type", filter);
      const basePath = props.apiBasePath ?? "/api/connectors";
      const data = await apiFetch<{ events: ConnectorEventRow[] }>(`${basePath}/${encodeURIComponent(props.connectorId)}/events?${query.toString()}`, {
        headers: props.headers
      });
      setRows(data.events);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.connectorId, filter]);

  useEffect(() => {
    let last = 0;
    function maybeRefresh() {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - last < 15_000) return;
      last = now;
      void load({ silent: true });
    }
    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", maybeRefresh);
    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", maybeRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.connectorId, filter]);

  async function loadExecution(row: ConnectorEventRow) {
    const executionLogId = row.execution_log_id;
    if (!executionLogId) return;
    setExecutionLoadingByEventId((prev) => ({ ...prev, [row.id]: true }));
    try {
      const entries = await fetchExecutionLog({ id: executionLogId, limit: 1 });
      setExecutionByEventId((prev) => ({ ...prev, [row.id]: entries[0] ?? null }));
    } catch {
      setExecutionByEventId((prev) => ({ ...prev, [row.id]: null }));
    } finally {
      setExecutionLoadingByEventId((prev) => ({ ...prev, [row.id]: false }));
    }
  }

  return (
    <div className="card connectorEventLog">
      <div className="toolbar connectorEventLogToolbar">
        <div>
          <div className="connectorEventLogTitle">{props.connectorName}</div>
          <div className="muted connectorEventLogSubtitle">
            <span className={STATUS_PILL_CLASSES[props.connectorStatus]}>
              {STATUS_LABELS[props.connectorStatus]}
            </span>
          </div>
        </div>
        <div className="toolbarGroup">
          <label className="field connectorEventLogFilter">
            <span className="label">Log type</span>
            <select className="select" value={filter} onChange={(e) => setFilter(e.target.value as ConnectorEventTypeFilter)}>
              <option value="all">All</option>
              <option value="connectivity">Connectivity</option>
              <option value="execution">Execution</option>
              <option value="command">Command</option>
              <option value="state">State</option>
            </select>
          </label>
        </div>
      </div>

      {error ? <ErrorState detail={error} /> : null}
      {loading ? <LoadingState /> : null}
      {!loading && rows.length === 0 ? <div className="muted">No events yet.</div> : null}

      {!loading && rows.length > 0 ? (
        <div className="tableWrap">
          <table className="table connectorEventLogTable">
            <thead>
              <tr>
                <th />
                <th>Time</th>
                <th>Type</th>
                <th>Level</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isExpanded = expandedId === row.id;
                return (
                  <Fragment key={row.id}>
                    <tr>
                      <td>
                        <button
                          className="btn btnSm connectorEventLogExpandBtn"
                          type="button"
                          onClick={() => setExpandedId((prev) => (prev === row.id ? null : row.id))}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? "Hide" : "Show"}
                        </button>
                      </td>
                      <td className="muted mono" title={row.ts}>{fmtTimestamp(row.ts)}</td>
                      <td>
                        <span className="pill pillNeutral">{labelCase(row.type)}</span>
                      </td>
                      <td>
                        <span className={`pill ${row.level === "error" ? "pillBad" : row.level === "warn" ? "pillWarn" : "pillNeutral"}`}>
                          {labelCase(row.level)}
                        </span>
                      </td>
                      <td className="mono connectorEventLogMessage">{row.message}</td>
                    </tr>
                    {isExpanded ? (
                      <tr>
                        <td colSpan={5}>
                          {/*
                            The execution is offered as a BUTTON and nothing else.
                            This used to render `Execution: {row.execution_log_id}`
                            beside it — a raw UUID as visible text, which the
                            no-UUID rule forbids outright. There is no honest
                            replacement label: the event row carries only the id,
                            and `fetchExecutionLog` answers with `operation_id` /
                            `node_id` — ids again, no names — so naming the
                            execution here would need a hub-side name join, not a
                            console change. The button's presence already says
                            what the pill said ("this event has an execution"),
                            because it renders only when the id is non-null.
                          */}
                          {row.execution_log_id ? (
                            <div style={{ marginBottom: 8, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                              <button
                                className="btn btnSm"
                                type="button"
                                disabled={executionLoadingByEventId[row.id] === true}
                                onClick={() => void loadExecution(row)}
                              >
                                {executionLoadingByEventId[row.id] ? "Loading..." : "View execution"}
                              </button>
                            </div>
                          ) : null}
                          <pre className="mono connectorEventLogDetails">
                            {JSON.stringify(row.details ?? {}, null, 2)}
                          </pre>
                          {executionByEventId[row.id] ? (
                            <pre className="mono connectorEventLogDetails" style={{ marginTop: 8 }}>
                              {JSON.stringify(executionByEventId[row.id], null, 2)}
                            </pre>
                          ) : null}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
